/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { conditionalString, sortAsc } from '@strata/helper-functions';
import { GridWithFilters } from '@strata/react-grid';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';
import { SuccessToast } from '@strata/react-toast-message';
import { ComponentProps } from 'react';

import { MED_BILLING_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import {
    checkBatchSubmission,
    ClaimFrequencyType,
    ClaimPlaceOfServiceMap,
    ClaimSubmissionType,
    ClaimSubmissionTypes,
    ClaimUomMap,
    strToNumber,
} from 'services/MedicalBillingConstants';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagType = {
    CLAIMS: 'CLAIMS',
    CLAIM_DETAILS: 'CLAIM_DETAILS',
    REFERRAL_CLAIMS: 'REFERRAL_CLAIMS',
} as const;

export interface ClaimSummary {
    transaction_id: string;
    resubmitted_transaction_id?: string;
    supplier_npi: string;
    payer_id: string;
    claim_id: string;
    referral_id: string;
    submitted_date: string;
    product_service_display_name: string;
    winningbid_rate?: string;
    claim_frequency_type: ClaimFrequencyType;
    start_date_of_service: string;
    end_date_of_service: string;
    submitted_amount: string;
    paid_amount: string;
    first_name: string;
    last_name: string;
    claim_status: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
}

interface ClaimIssue {
    category_code_1: string;
    status_code_1: string;
    entity_identifier_code_1: string;
    category_code_2?: string;
    status_code_2?: string;
    entity_identifier_code_2?: string;
    category_code_1_desc?: string;
    status_code_1_desc?: string;
    entity_identifier_code_1_desc?: string;
    category_code_2_desc?: string;
    status_code_2_desc?: string;
    entity_identifier_code_2_desc?: string;
}

interface ServiceIssue extends ClaimIssue {
    procedure_code: string;
}

interface SegmentIssue {
    segment: string;
    segment_position: string;
    loop: string;
    IK304_error_code: string;
    IK304_error_code_desc: string;
}

interface ElementIssue {
    element_position: string;
    bad_element: string;
    IK403_error_code: string;
    IK403_error_code_desc: string;
}

interface ClaimDetailsAPIResponse {
    claim_id: string;
    referral_id: string;
    claim_status: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
    transaction_id: string;
    submitted_date: string;
    original_claim_number?: string;
    submission_type: ClaimSubmissionType;
    supplier_npi: string;
    taxonomy_code: string;
    place_of_service: keyof typeof ClaimPlaceOfServiceMap;
    patient_account_number: string;
    claim_frequency_type: ClaimFrequencyType;
    provider_signature_on_file: string;
    provider_accept_assignment: string;
    assignment_of_benefits: string;
    release_of_information: string;
    diagnoses: string[];
    submitted_amount?: string;
    contractual_obligation: string;
    payer_amount?: string;
    other_adjustments: string;
    member_liability?: string;
    adjustment_total: string;
    claim_total: string;
    member_id: string;
    claim_category_code?: string;
    claim_category_code_desc?: string;
    claim_status_code?: string;
    claim_status_code_desc?: string;
    secondary_claim_category_code?: string;
    secondary_claim_status_code?: string;
    services: {
        date_of_service: string;
        date_of_service_end: string;
        procedure_code: string;
        procedure_code_description: string;
        modifier_codes: string[];
        ndc?: string;
        diagnoses: string[];
        dispensed_quantity?: string;
        dispensed_uom?: keyof typeof ClaimUomMap;
        billing_quantity: string;
        billing_uom: keyof typeof ClaimUomMap;
        provider_charge: string;
        submitted_amount: string;
        contractual_obligation: string;
        payer_amount: string;
        other_adjustments: string;
        member_liability: string;
        adjustment_total: string;
        total?: string;
        service_status?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
        service_status_code?: string;
        service_status_code_desc?: string;
        service_category_code?: string;
        service_category_code_desc?: string;
        secondary_service_status_code?: string;
        secondary_service_status_code_desc?: string;
        secondary_service_category_code?: string;
        secondary_service_category_code_desc?: string;
        entity_identifier_code?: string;
        prescriber_last_name?: string;
        adjustments: {
            adjustment_group_code: string;
            adjustment_group_code_desc: string;
            adjustment_reason_code: string;
            adjustment_reason_code_desc: string;
        }[];
    }[];
    adjustments: {
        adjustment_group_code: string;
        adjustment_group_code_desc: string;
        adjustment_reason_code: string;
        adjustment_reason_code_desc: string;
    }[];
    claim_issues: ClaimIssue[];
    service_issues: ServiceIssue[];
    data_segment_issues: SegmentIssue[];
    data_element_issues: ElementIssue[];
}

export interface ClaimDetails {
    claimId: string;
    payerId: string;
    referralId: string;
    transactionId: string;
    caseId?: string;
    submittedDate: string;
    originalClaimNumber?: string;
    submissionType: ClaimSubmissionType;
    supplierNpi: string;
    taxonomyCode: string;
    placeOfService: keyof typeof ClaimPlaceOfServiceMap;
    patientAccountNumber: string;
    claimFrequencyType: ClaimFrequencyType;
    providerSignatureOnFile: string;
    providerAcceptAssignment: string;
    assignmentOfBenefits: string;
    releaseOfInformation: string;
    diagnoses: string[];
    submittedAmount?: number;
    contractualObligation?: number;
    payerAmount?: number;
    otherAdj?: number;
    memberLiability?: number;
    adjTotal?: number;
    claimTotal: number;
    memberId: string;
    claimStatus: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
    claimStatusCodes: string[];
    services: {
        dateOfService: string;
        dateOfServiceEnd: string;
        procedureCode: string;
        procedureCodeDescription: string;
        procedureCodeDisplayText: string;
        modifierCodes: string[];
        ndc?: string;
        diagnoses: string[];
        dispensedQty?: number;
        dispensedQtyUom?: keyof typeof ClaimUomMap;
        billingQty: number;
        billingQtyUom: keyof typeof ClaimUomMap;
        providerCharge: number;
        submittedAmount: number;
        contractualObligation?: number;
        payerAmount?: number;
        otherAdj?: number;
        memberLiability?: number;
        adjTotal?: number;
        total?: number;
        adjCodes: string[];
        serviceStatus?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
        serviceStatusCodes: string[];
        secondaryServiceStatusCodes: string[];
    }[];
    adjCodes: {
        code: string;
        description: string;
        code2: string;
        description2: string;
    }[];
    claimIssues: {
        code: string;
        description: string;
        code2: string | undefined;
        description2: string;
    }[];
    serviceIssues: Record<
        string,
        {
            code: string;
            description: string;
            code2: string | undefined;
            description2: string;
        }[]
    >;
    dataSegmentIssues: {
        code: string;
        description: string;
    }[];
    dataElementIssues: {
        code: string;
        description: string;
    }[];
}

export interface ClaimForReferral {
    transactionId: string;
    claimId: string;
    processedDate: string;
    submittedAmount: number;
    contractualObligation?: number;
    payerAmount?: number;
    otherAdj?: number;
    memberLiability?: number;
    adjTotal?: number;
    claimTotal: number;
    claimStatus?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
    isEstimate: boolean;
}

export const formatCodeDescLabel = <T extends string | undefined>(
    code: T,
    desc: string | undefined,
    descFirst = false,
): T extends string ? string : undefined => {
    if (!code) {
        return undefined as T extends string ? string : undefined;
    }

    if (descFirst) {
        const descStr = conditionalString(!!desc, `${desc} - `);
        return `${descStr}${code}` as T extends string ? string : undefined;
    }

    const descStr = conditionalString(!!desc, ` - ${desc}`);
    return `${code}${descStr}` as T extends string ? string : undefined;
};

export const formatDiagnosisLabel = <T extends string | undefined>(
    code: T,
    diagnosesList: { code: string; description?: string }[] | undefined,
) => {
    const diagnosis = diagnosesList?.find((dx) => dx.code === code);
    return formatCodeDescLabel(code, diagnosis?.description, true);
};

const formatStatusCodes = ({
    categoryCode,
    categoryCodeDesc,
    statusCode,
    statusCodeDesc,
}: {
    categoryCode?: string;
    categoryCodeDesc?: string;
    statusCode?: string;
    statusCodeDesc?: string;
}) => [
    ...(categoryCode ? [formatCodeDescLabel(categoryCode, categoryCodeDesc)] : []),
    ...(statusCode ? [formatCodeDescLabel(statusCode, statusCodeDesc)] : []),
];

const formatErrorDescription = (items: { title: string; values: (string | undefined)[] }[]) =>
    items.reduce(
        (accumulator, item, index) =>
            `${accumulator}${
                item.values.filter((val) => val).length > 0
                    ? `${conditionalString(index > 0, ', ')}${item.title}: ${item.values
                          .filter((val) => val)
                          .join(' - ')}`
                    : ''
            }`,
        '',
    );

const formatClaimServiceIssue = (issue: ClaimIssue) => ({
    code: formatCodeDescLabel(issue.category_code_1, issue.category_code_1_desc),
    description: formatErrorDescription([
        {
            title: 'Status',
            values: [issue.status_code_1, issue.status_code_1_desc],
        },
        {
            title: 'Entity',
            values: [issue.entity_identifier_code_1, issue.entity_identifier_code_1_desc],
        },
    ]),
    code2: formatCodeDescLabel(issue.category_code_2, issue.category_code_2_desc),
    description2: formatErrorDescription([
        {
            title: 'Status',
            values: [issue.status_code_2, issue.status_code_2_desc],
        },
        {
            title: 'Entity',
            values: [issue.entity_identifier_code_2, issue.entity_identifier_code_2_desc],
        },
    ]),
});

const mapClaimDetails = (result: ClaimDetailsAPIResponse, payerId: string): ClaimDetails => {
    const isEstimate = result.submission_type === ClaimSubmissionTypes.ESTIMATE;

    return {
        transactionId: result.transaction_id,
        claimId: result.claim_id,
        payerId,
        originalClaimNumber: result.original_claim_number,
        referralId: result.referral_id,
        submissionType: result.submission_type,
        submittedDate: result.submitted_date,
        supplierNpi: result.supplier_npi,
        taxonomyCode: result.taxonomy_code,
        memberId: result.member_id,
        placeOfService: result.place_of_service,
        patientAccountNumber: result.patient_account_number,
        claimFrequencyType: result.claim_frequency_type,
        providerSignatureOnFile: result.provider_signature_on_file,
        providerAcceptAssignment: result.provider_accept_assignment,
        assignmentOfBenefits: result.assignment_of_benefits,
        releaseOfInformation: result.release_of_information,
        diagnoses: result.diagnoses,
        claimStatus:
            result.claim_status?.trim() as (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses],
        claimStatusCodes: formatStatusCodes({
            categoryCode: result.claim_category_code,
            categoryCodeDesc: result.claim_category_code_desc,
            statusCode: result.claim_status_code,
            statusCodeDesc: result.claim_status_code_desc,
        }),
        submittedAmount: strToNumber(result.submitted_amount),
        contractualObligation: checkBatchSubmission(
            result.contractual_obligation,
            payerId,
            isEstimate,
        ),
        payerAmount: checkBatchSubmission(result.payer_amount, payerId, isEstimate),
        otherAdj: checkBatchSubmission(result.other_adjustments, payerId, isEstimate),
        memberLiability: checkBatchSubmission(result.member_liability, payerId, isEstimate),
        adjTotal: checkBatchSubmission(result.adjustment_total, payerId, isEstimate),
        claimTotal: strToNumber(result.claim_total),
        services: result.services?.map((service) => ({
            dateOfService: service.date_of_service,
            dateOfServiceEnd: service.date_of_service_end,
            procedureCode: service.procedure_code,
            procedureCodeDescription: service.procedure_code_description,
            procedureCodeDisplayText: formatCodeDescLabel(
                service.procedure_code,
                service.procedure_code_description,
                true,
            ),
            modifierCodes: service.modifier_codes,
            ndc: service.ndc,
            diagnoses: service.diagnoses,
            dispensedQty: strToNumber(service.dispensed_quantity),
            dispensedQtyUom: service.dispensed_uom,
            billingQty: strToNumber(service.billing_quantity),
            billingQtyUom: service.billing_uom,
            providerCharge: strToNumber(service.provider_charge),
            submittedAmount: strToNumber(service.submitted_amount),
            contractualObligation: checkBatchSubmission(
                service.contractual_obligation,
                payerId,
                isEstimate,
            ),
            payerAmount: checkBatchSubmission(service.payer_amount, payerId, isEstimate),
            otherAdj: checkBatchSubmission(service.other_adjustments, payerId, isEstimate),
            memberLiability: checkBatchSubmission(service.member_liability, payerId, isEstimate),
            adjTotal: checkBatchSubmission(service.adjustment_total, payerId, isEstimate),
            total: strToNumber(service.total),
            adjCodes: service.adjustments
                ?.map((code) =>
                    [code.adjustment_group_code, code.adjustment_reason_code].join(' - '),
                )
                .sort(sortAsc),
            serviceStatus: service.service_status,
            serviceStatusCodes: formatStatusCodes({
                categoryCode: service.service_category_code,
                categoryCodeDesc: service.service_category_code_desc,
                statusCode: service.service_status_code,
                statusCodeDesc: service.service_status_code_desc,
            }),
            secondaryServiceStatusCodes: formatStatusCodes({
                categoryCode: service.secondary_service_category_code,
                categoryCodeDesc: service.secondary_service_category_code_desc,
                statusCode: service.secondary_service_status_code,
                statusCodeDesc: service.secondary_service_status_code_desc,
            }),
        })),
        adjCodes: result.adjustments
            // Filter out duplicate codes
            ?.filter(
                (code, index, arr) =>
                    arr.findIndex(
                        (c) =>
                            code.adjustment_group_code === c.adjustment_group_code &&
                            code.adjustment_group_code_desc === c.adjustment_group_code_desc &&
                            code.adjustment_reason_code === c.adjustment_reason_code &&
                            code.adjustment_reason_code_desc === c.adjustment_reason_code_desc,
                    ) === index,
            )
            .map((code) => ({
                code: code.adjustment_group_code,
                description: code.adjustment_group_code_desc,
                code2: code.adjustment_reason_code,
                description2: code.adjustment_reason_code_desc,
            })),
        // 277
        claimIssues: result.claim_issues.map(formatClaimServiceIssue),
        serviceIssues: (() => {
            const serviceIssuesMap: ClaimDetails['serviceIssues'] = {};
            result.service_issues.forEach((issue) => {
                serviceIssuesMap[issue.procedure_code] = [
                    ...(serviceIssuesMap[issue.procedure_code] ?? []),
                    formatClaimServiceIssue(issue),
                ];
            });
            return serviceIssuesMap;
        })(),
        // 999
        dataSegmentIssues: result.data_segment_issues.map((issue) => ({
            code: formatCodeDescLabel(issue.IK304_error_code, issue.IK304_error_code_desc),
            description: formatErrorDescription([
                { title: 'Segment', values: [issue.segment] },
                { title: 'Position', values: [issue.segment_position] },
                { title: 'Loop', values: [issue.loop] },
            ]),
        })),
        dataElementIssues: result.data_element_issues.map((issue) => ({
            code: formatCodeDescLabel(issue.IK403_error_code, issue.IK403_error_code_desc),
            description: formatErrorDescription([
                { title: 'Position', values: [issue.element_position] },
                { title: 'Element', values: [issue.bad_element] },
            ]),
        })),
    };
};

export const medbillingApi = createApi({
    baseQuery: getBaseQuery(MED_BILLING_API_URL, true),
    refetchOnMountOrArgChange: true,
    reducerPath: 'medbillingApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getClaims: build.query<
            {
                claims: ClaimSummary[];
                count: number;
                filters: Record<string, string[]>;
            },
            {
                page: number;
                searchText?: string;
                activeFilters?: ComponentProps<typeof GridWithFilters>['activeFilters'];
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({ page, searchText, activeFilters, sort }) => ({
                url: 'transactions',
                method: 'POST',
                body: {
                    page,
                    search_text: searchText,
                    filters: activeFilters,
                    sort_id: sort?.[0].id,
                    sort_dir: sort?.[0].desc ? 'desc' : 'asc',
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving claims.'),
            providesTags: [TagType.CLAIMS],
        }),
        /** Gets claim/estimate details */
        getClaimDetails: build.query<ClaimDetails, { payerId: string; transactionId: string }>({
            query: ({ payerId, transactionId }) => `${payerId}/transactions/${transactionId}`,
            transformResponse: (response: ClaimDetailsAPIResponse, _, { payerId }) =>
                mapClaimDetails(response, payerId),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving claim details.',
            ),
            providesTags: (_, __, { transactionId }) => [
                { type: TagType.CLAIM_DETAILS, id: transactionId },
            ],
        }),
        /** Gets claims & estimates for a referral */
        getClaimsForReferral: build.query<
            ClaimForReferral[],
            { payerId: string; referralId: string }
        >({
            query: ({ payerId, referralId }) => `${payerId}/referrals/${referralId}`,
            transformResponse: (
                response: {
                    claim_id: string;
                    transaction_id: string;
                    processed_date: string;
                    submitted_amount: string;
                    contractual_obligation?: string;
                    payer_amount: string;
                    other_adjustments?: string;
                    member_liability: string;
                    adjustment_total?: string;
                    claim_total: string;
                    claim_status?: (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses];
                    is_estimate: boolean;
                    estimate_expiration?: string;
                }[],
                _,
                { payerId },
            ) =>
                response?.map((claim) => ({
                    transactionId: claim.transaction_id,
                    claimId: claim.claim_id,
                    processedDate: claim.processed_date,
                    submittedAmount: strToNumber(claim.submitted_amount),
                    contractualObligation: checkBatchSubmission(
                        claim.contractual_obligation,
                        payerId,
                        claim.is_estimate,
                    ),
                    payerAmount: checkBatchSubmission(
                        claim.payer_amount,
                        payerId,
                        claim.is_estimate,
                    ),
                    otherAdj: checkBatchSubmission(
                        claim.other_adjustments,
                        payerId,
                        claim.is_estimate,
                    ),
                    memberLiability: checkBatchSubmission(
                        claim.member_liability,
                        payerId,
                        claim.is_estimate,
                    ),
                    adjTotal: checkBatchSubmission(
                        claim.adjustment_total,
                        payerId,
                        claim.is_estimate,
                    ),
                    claimTotal: strToNumber(claim.claim_total),
                    claimStatus:
                        claim.claim_status?.trim() as (typeof ClaimStatusIndicator.Statuses)[keyof typeof ClaimStatusIndicator.Statuses],
                    isEstimate: claim.is_estimate,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving claims information.',
            ),
            providesTags: (_result, _error, { referralId }) => [
                { type: TagType.REFERRAL_CLAIMS, id: referralId },
            ],
        }),
        postClaimStatusCheck: build.mutation<
            ClaimDetails,
            {
                payerId: string;
                referralId: string;
                transactionId: string;
            }
        >({
            query: ({ payerId, transactionId }) => ({
                url: `${payerId}/transactions/${transactionId}`,
                method: 'POST',
            }),
            invalidatesTags: (_, error, { referralId, transactionId }) =>
                !error
                    ? [
                          TagType.CLAIMS,
                          { type: TagType.CLAIM_DETAILS, id: transactionId },
                          { type: TagType.REFERRAL_CLAIMS, id: referralId },
                      ]
                    : [],
            transformResponse: (response: ClaimDetailsAPIResponse, _, { payerId }) => {
                SuccessToast({ message: 'Claim was updated successfully.' });
                return mapClaimDetails(response, payerId);
            },
            transformErrorResponse: handleRTKQueryError('An error occurred updating claim status.'),
        }),
    }),
});

export const {
    useGetClaimsQuery,
    useGetClaimDetailsQuery,
    useGetClaimsForReferralQuery,
    usePostClaimStatusCheckMutation,
} = medbillingApi;
